import { useMemo } from 'react';
import { getTextFactory } from 'utils/TextUtils';
import { AcmEligibilityResponse } from '../types';
import {
  getAccountLabel,
  getRuleMessageKey,
} from 'components/Alerts/AccountEligibilityRulesAlert/utils/accountEligibilityRules.utils';

export type EligibilityError = {
  ruleCode: string; // e.g., 'BR0001'
  messageKey: string; // e.g., 'BR0001', 'BR0002', 'BR0002.onlyABorBC', 'BR0002.onlyNB', 'BR0002.mixedAB', 'BR0002.mixedBC', 'BR0002.mixedNB'
  failedAccounts: string[]; // e.g., ['Cash, Canadian Dollar (CAD)', ...]
};

type UseEligibilityGuardParams = {
  getGlobalText: ReturnType<typeof getTextFactory>;
  acmEligibilityResponses?: AcmEligibilityResponse[];
};

/**
 * Maps acmEligibilityResponses (HTTP 200 OK) to eligibility business-rule banners.
 *
 * Returns one EligibilityError entry per matched rule code, each with the list
 * of account labels that failed that specific rule.
 */
export const useEligibilityGuard = ({
  getGlobalText,
  acmEligibilityResponses,
}: UseEligibilityGuardParams): { eligibilityErrors: EligibilityError[] } => {
  const eligibilityErrors = useMemo((): EligibilityError[] => {
    if (!acmEligibilityResponses?.length) return [];

    // Group account labels by rule code
    const ruleToAccounts = new Map<string, string[]>();
    for (const { account, failedRules } of acmEligibilityResponses) {
      const accountLabel = getAccountLabel(account, getGlobalText);
      for (const ruleCode of failedRules) {
        const accounts = ruleToAccounts.get(ruleCode) ?? [];
        ruleToAccounts.set(ruleCode, accounts);
        accounts.push(accountLabel);
      }
    }

    return Array.from(ruleToAccounts.entries()).map(([ruleCode, failedAccounts]) => ({
      ruleCode,
      messageKey: getRuleMessageKey(ruleCode, acmEligibilityResponses),
      failedAccounts,
    }));
  }, [acmEligibilityResponses, getGlobalText]);

  return { eligibilityErrors };
};
